import ApiService from "@/core/services/api.service";
import {
    STATUS_TEXT,
    updateStatusTextFromApi,
    parseCvStatusResponse,
} from "@/core/config/recruitmentCvs/recruitmentCvsOption";

/**
 * Store module cho trang "Quản lý hồ sơ ứng viên" (recruitment-cvs).
 *
 * Dữ liệu hồ sơ ứng viên (RecruitmentCurriculumVitae):
 *   id, job_id, name, email, phone, address, cv_file, file, media, message, type, status, created_at, updated_at
 */

// action types
export const RCV_GET_CVS = "job-recruitment/cvs";
export const RCV_GET_CV_DETAIL = "get-recruitment-cv-detail";
export const RCV_CREATE_CV = "create-recruitment-cv";
export const RCV_UPDATE_CV = "update-recruitment-cv";
export const RCV_DELETE_CV = "delete-recruitment-cv";
export const RCV_UPDATE_CV_STATUS = "update-recruitment-cv-status";
export const RCV_GET_CV_STATUS = "job-recruitment/cv-status";

// mutation types
export const RCV_SET_CVS = "rcvSetCvs";
export const RCV_SET_PAGINATION = "rcvSetPagination";
export const RCV_SET_CURRENT_CV = "rcvSetCurrentCv";
export const RCV_SET_LOADING = "rcvSetLoading";
export const RCV_UPSERT_CV = "rcvUpsertCv";
export const RCV_REMOVE_CV = "rcvRemoveCv";
export const RCV_SET_CV_STATUS_MAP = "rcvSetCvStatusMap";

const state = {
    cvList: [],
    pagination: {
        total: 0,
        currentPage: 1,
        perPage: 15,
        lastPage: 1,
    },
    currentCv: null,
    loading: false,
    cvStatusMap: {},
};

const getters = {
    rcvList(state) {
        return state.cvList;
    },
    rcvPagination(state) {
        return state.pagination;
    },
    rcvCurrentCv(state) {
        return state.currentCv;
    },
    rcvLoading(state) {
        return state.loading;
    },
    rcvStatusMap(state) {
        return state.cvStatusMap;
    },
};

/** Chuẩn hóa danh sách dữ liệu và meta phân trang từ response API */
function extractResponseListAndPagination(resData) {
    let list = [];
    let meta = {};

    if (!resData) {
        return { list: [], pagination: { total: 0, currentPage: 1, perPage: 15, lastPage: 1 } };
    }

    // Trường hợp 1: Laravel standard paginator wrapped trong response format { error, message, data: { current_page, data: [...], total, per_page, last_page } }
    if (resData.data && typeof resData.data === "object" && Array.isArray(resData.data.data)) {
        list = resData.data.data;
        meta = resData.data;
    }
    // Trường hợp 2: resData.data là mảng trực tiếp { error, message, data: [...] }
    else if (Array.isArray(resData.data)) {
        list = resData.data;
        meta = resData.pagination || (resData.meta && resData.meta.pagination) || resData.meta || {};
    }
    // Trường hợp 3: resData là mảng trực tiếp [...]
    else if (Array.isArray(resData)) {
        list = resData;
        meta = {};
    }
    // Trường hợp 4: resData.data là object nhưng có key list / items / records
    else if (resData.data && typeof resData.data === "object") {
        meta = resData.data;
        if (Array.isArray(resData.data.items)) list = resData.data.items;
        else if (Array.isArray(resData.data.list)) list = resData.data.list;
        else if (Array.isArray(resData.data.records)) list = resData.data.records;
    }

    const total = meta.total !== undefined ? Number(meta.total) : list.length;
    const perPage = Number(meta.per_page || meta.perPage || meta.limit || 15);
    const currentPage = Number(meta.current_page || meta.currentPage || meta.page || 1);
    const lastPage = Number(meta.last_page || meta.lastPage || Math.max(1, Math.ceil(total / (perPage || 15))));

    return {
        list,
        pagination: {
            total,
            currentPage,
            perPage,
            lastPage,
        },
    };
}

/** Chuẩn hóa meta phân trang */
function parsePagination(data, rows = []) {
    const parsed = extractResponseListAndPagination(data);
    if (parsed && parsed.pagination && parsed.pagination.total !== undefined) {
        return parsed.pagination;
    }
    const meta = (data && data.pagination) || (data && data.meta && data.meta.pagination) || (data && data.meta) || data || {};
    return {
        total: meta.total !== undefined ? Number(meta.total) : rows.length,
        currentPage: Number(meta.current_page || meta.currentPage || 1),
        perPage: Number(meta.per_page || meta.perPage || 15),
        lastPage: Number(meta.last_page || meta.lastPage || 1),
    };
}

/** Chuẩn hóa 1 bản ghi hồ sơ CV */
export function normalizeCvItem(item) {
    if (!item || typeof item !== "object") return null;

    // Chuẩn hóa media đính kèm
    const mediaList = Array.isArray(item.media)
        ? item.media.map((m) => ({
              ...m,
              url: m.url || m.original_url || m.preview_url || "",
          }))
        : [];

    // Tìm URL file CV tốt nhất (ưu tiên cv_file, sau đó media, sau đó file)
    let cvFile = item.cv_file || item.file || null;
    if (!cvFile && mediaList.length > 0) {
        cvFile = mediaList[0].url || mediaList[0].original_url || null;
    }

    const statusNum = item.status !== undefined && item.status !== null ? Number(item.status) : 0;

    return {
        id: item.id,
        job_id: item.job_id || item.request_id || item.order_id || null,
        order_id: item.order_id || item.job_id || item.request_id || null,
        order_title: item.order_title || "",
        raw_job_id: item.raw_job_id || item.job_id || null,
        name: item.name || item.full_name || "",
        email: item.email || "",
        phone: item.phone || item.phone_number || "",
        address: item.address || "",
        cv_file: cvFile,
        file: item.file || null,
        media: mediaList,
        message: item.message || item.note || item.cover_letter || "",
        type: item.type !== undefined ? Number(item.type) : 0,
        source_type: item.source_type !== undefined && item.source_type !== null ? Number(item.source_type) : null,
        preliminary_info: item.preliminary_info || item.preliminary_note || "",
        status: statusNum,
        status_text: item.status_text || item.status_label || STATUS_TEXT[statusNum] || `Trạng thái #${statusNum}`,
        hr_approve: item.hr_approve !== undefined && item.hr_approve !== null ? Number(item.hr_approve) : null,
        teamlead_approve: item.teamlead_approve !== undefined && item.teamlead_approve !== null ? Number(item.teamlead_approve) : null,
        hr_reason_reject: item.hr_reason_reject || "",
        team_lead_reason_reject: item.team_lead_reason_reject || "",
        official_salary: item.official_salary !== undefined && item.official_salary !== null ? item.official_salary : null,
        salary: item.salary !== undefined && item.salary !== null ? item.salary : null,
        probation_date: item.probation_date || "",
        probation_reason: item.probation_reason || "",
        probation_exit_date: item.probation_exit_date || "",
        probation_exit_reason: item.probation_exit_reason || "",
        probation_result: item.probation_result || null,
        probation_note: item.probation_note || "",
        interview_v1_result: item.interview_v1_result || null,
        interview_v1_note: item.interview_v1_note || item.team_lead_feedback || "",
        team_lead_feedback: item.team_lead_feedback || item.interview_v1_note || "",
        result_note: item.result_note || item.team_lead_feedback || item.interview_v1_note || "",
        interview_v1_score: item.interview_v1_score || null,
        interview_v2_result: item.interview_v2_result || null,
        interview_v2_note: item.interview_v2_note || "",
        interview_v2_score: item.interview_v2_score || null,
        interview_data: item.interview_data || null,
        round: item.round !== undefined && item.round !== null ? Number(item.round) : null,
        date: item.date || item.interview_date || null,
        start_time: item.start_time || item.interview_start_time || null,
        end_time: item.end_time || item.interview_end_time || null,
        cancel_reason: item.cancel_reason || item.reason || "",
        cancel_interview_v1_reason: item.cancel_interview_v1_reason || "",
        cancel_interview_v2_reason: item.cancel_interview_v2_reason || "",
        created_at: item.created_at || "",
        updated_at: item.updated_at || "",
    };
}

/** Tạo FormData cho payload có file CV upload */
function buildFormData(payload) {
    const form = new FormData();
    Object.keys(payload || {}).forEach((key) => {
        const val = payload[key];
        if (val === undefined || val === null) return;
        if (Array.isArray(val)) {
            val.forEach((item) => {
                if (item !== undefined && item !== null) {
                    form.append(`${key}[]`, item);
                }
            });
        } else {
            form.append(key, val);
        }
    });
    return form;
}

const actions = {
    [RCV_GET_CVS](context, payload = {}) {
        context.commit(RCV_SET_LOADING, true);
        const limit = payload.limit || payload.per_page || 15;
        const targetJobId = payload.job_id !== undefined && payload.job_id !== null && payload.job_id !== ""
            ? payload.job_id
            : (payload.recruitment_job_id !== undefined && payload.recruitment_job_id !== null && payload.recruitment_job_id !== "" ? payload.recruitment_job_id : undefined);

        const params = {
            page: payload.page || 1,
            limit: limit,
            per_page: limit,
            keyword: payload.keyword || undefined,
            job_id: targetJobId,
            department_id: payload.department_id || undefined,
            person_in_charge_id: payload.person_in_charge_id || undefined,
            teamlead_id: payload.teamlead_id || undefined,
            type: payload.type !== undefined && payload.type !== "" ? payload.type : undefined,
            status: payload.status !== undefined && payload.status !== "" ? payload.status : undefined,
            start_date: payload.start_date || undefined,
            end_date: payload.end_date || undefined,
        };

        // Loại bỏ các key undefined hoặc rỗng để URL query sạch đẹp
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === "") {
                delete params[key];
            }
        });

        const endpoints = [
            "job-recruitment/cvs",
            "job-recruitment-cvs",
            "noibo/job-recruitment-cvs",
            "recruitment-cvs",
            "job-recruitment/requests/candidates",
            "job-recruitment-requests/candidates",
            "candidates",
        ];

        const queryEndpoint = (idx = 0) => {
            if (idx >= endpoints.length) {
                context.commit(RCV_SET_CVS, []);
                context.commit(RCV_SET_LOADING, false);
                return Promise.reject(new Error("Không tìm thấy endpoint hồ sơ CV"));
            }
            const ep = endpoints[idx];
            return ApiService.query(ep, params)
                .then(({ data }) => {
                    const { list: rawList, pagination } = extractResponseListAndPagination(data);
                    const list = rawList.map(normalizeCvItem).filter(Boolean);
                    context.commit(RCV_SET_CVS, list);
                    context.commit(RCV_SET_PAGINATION, pagination);
                    context.commit(RCV_SET_LOADING, false);
                    return data;
                })
                .catch((err) => {
                    if (err && err.response && [404, 405].includes(err.response.status) && idx < endpoints.length - 1) {
                        return queryEndpoint(idx + 1);
                    }
                    context.commit(RCV_SET_CVS, []);
                    context.commit(RCV_SET_LOADING, false);
                    return Promise.reject(err);
                });
        };

        return new Promise((resolve, reject) => {
            queryEndpoint(0)
                .then(resolve)
                .catch(reject);
        });
    },

    [RCV_GET_CV_DETAIL](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            ApiService.get("job-recruitment/cvs", id)
                .then(({ data }) => {
                    const item = normalizeCvItem(data.data || data);
                    context.commit(RCV_SET_CURRENT_CV, item);
                    resolve(data);
                })
                .catch(() => {
                    ApiService.get("job-recruitment-cvs", id)
                        .then(({ data }) => {
                            const item = normalizeCvItem(data.data || data);
                            context.commit(RCV_SET_CURRENT_CV, item);
                            resolve(data);
                        })
                        .catch(() => {
                            ApiService.get("recruitment-cvs", id)
                                .then(({ data }) => {
                                    const item = normalizeCvItem(data.data || data);
                                    context.commit(RCV_SET_CURRENT_CV, item);
                                    resolve(data);
                                })
                                .catch(({ response }) => reject(response));
                        });
                });
        });
    },

    [RCV_CREATE_CV](context, payload) {
        const { file, cv_file, files, cv_files, ...rest } = payload || {};
        return new Promise((resolve, reject) => {
            const rawFiles = Array.isArray(cv_file) && cv_file.length > 0
                ? cv_file
                : (Array.isArray(files) && files.length > 0
                    ? files
                    : (Array.isArray(cv_files) && cv_files.length > 0
                        ? cv_files
                        : (cv_file instanceof File ? [cv_file] : (file instanceof File ? [file] : []))));
            const hasUpload = rawFiles.length > 0;
            const singleFile = rawFiles[0] || null;
            const finalJobId = (rest.recruitment_job_id !== undefined && rest.recruitment_job_id !== null && rest.recruitment_job_id !== "")
                ? rest.recruitment_job_id
                : (rest.job_id || rest.order_id || rest.request_id || null);
            const requestId = rest.order_id || rest.request_id || rest.job_id || null;

            const baseBody = {
                ...rest,
                job_id: finalJobId,
                recruitment_job_id: finalJobId,
                request_id: requestId,
                order_id: requestId,
            };

            let call;
            if (hasUpload) {
                const formData = new FormData();
                Object.keys(baseBody).forEach((key) => {
                    const val = baseBody[key];
                    if (val === undefined || val === null) return;
                    if (Array.isArray(val)) {
                        val.forEach((item) => {
                            if (item !== undefined && item !== null) {
                                formData.append(`${key}[]`, item);
                            }
                        });
                    } else {
                        formData.append(key, val);
                    }
                });

                // Gửi đúng cv_file[] theo yêu cầu của API saveCv
                rawFiles.forEach((item) => {
                    formData.append("cv_file[]", item);
                });

                call = ApiService.upload("job-recruitment/requests/saveCv", formData);
            } else {
                call = ApiService.post("job-recruitment/requests/saveCv", { ...baseBody, cv_file: typeof cv_file === "string" ? cv_file : "" });
            }

            call.then(({ data }) => {
                if (data && (data.error === true || data.error === 1 || data.status === false || data.error_code || (data.data && data.data.message_validate_form) || data.message_validate_form)) {
                    reject(data);
                    return;
                }
                const item = normalizeCvItem(data.data || data);
                context.commit(RCV_UPSERT_CV, item);
                resolve(data);
            }).catch((err) => {
                const res = (err && err.response) || err;
                const resData = (res && res.data) || res;

                // Nếu API saveCv trả về message lỗi hoặc validation error (400, 422, message, msg, error, errors, message_validate_form...),
                // reject ngay với lỗi từ server để hiển thị đúng thông báo lỗi từ API
                if (
                    resData &&
                    (resData.message_validate_form ||
                        (resData.data && resData.data.message_validate_form) ||
                        resData.error ||
                        resData.message ||
                        resData.msg ||
                        resData.errors ||
                        (res && [400, 422, 403, 409, 500].includes(res.status)))
                ) {
                    reject(res);
                    return;
                }

                // Chỉ gọi fallback nếu 404/405 không tìm thấy route
                if (res && [404, 405].includes(res.status)) {
                    const fallbackUrl = "job-recruitment/cvs";
                    let fallbackCall;
                    if (hasUpload) {
                        const fallbackFormData = new FormData();
                        Object.keys(baseBody).forEach((key) => {
                            const val = baseBody[key];
                            if (val === undefined || val === null) return;
                            if (Array.isArray(val)) {
                                val.forEach((item) => {
                                    if (item !== undefined && item !== null) {
                                        fallbackFormData.append(`${key}[]`, item);
                                    }
                                });
                            } else {
                                fallbackFormData.append(key, val);
                            }
                        });
                        rawFiles.forEach((item) => {
                            fallbackFormData.append("cv_file[]", item);
                        });
                        fallbackCall = ApiService.upload(fallbackUrl, fallbackFormData);
                    } else {
                        fallbackCall = ApiService.post(fallbackUrl, { ...baseBody, cv_file: typeof cv_file === "string" ? cv_file : "" });
                    }

                    fallbackCall.then(({ data }) => {
                        if (data && (data.error === true || data.error === 1 || data.status === false || data.error_code || (data.data && data.data.message_validate_form) || data.message_validate_form)) {
                            reject(data);
                            return;
                        }
                        const item = normalizeCvItem(data.data || data);
                        context.commit(RCV_UPSERT_CV, item);
                        resolve(data);
                    }).catch((fallbackErr) => {
                        const fRes = (fallbackErr && fallbackErr.response) || fallbackErr;
                        reject(fRes || res);
                    });
                } else {
                    reject(res);
                }
            });
        });
    },

    [RCV_UPDATE_CV](context, payload) {
        const { id, file, cv_file, files, cv_files, ...rest } = payload || {};
        return new Promise((resolve, reject) => {
            const rawFiles = Array.isArray(cv_file) && cv_file.length > 0
                ? cv_file
                : (Array.isArray(files) && files.length > 0
                    ? files
                    : (Array.isArray(cv_files) && cv_files.length > 0
                        ? cv_files
                        : (cv_file instanceof File ? [cv_file] : (file instanceof File ? [file] : []))));
            const hasUpload = rawFiles.length > 0;

            const finalJobId = (rest.recruitment_job_id !== undefined && rest.recruitment_job_id !== null && rest.recruitment_job_id !== "")
                ? rest.recruitment_job_id
                : rest.job_id;

            const updateRest = {
                ...rest,
                ...(finalJobId !== undefined && finalJobId !== null ? { job_id: finalJobId, recruitment_job_id: finalJobId } : {}),
            };

            let call;
            if (hasUpload) {
                const formData = new FormData();
                Object.keys(updateRest).forEach((key) => {
                    const val = updateRest[key];
                    if (val === undefined || val === null) return;
                    if (Array.isArray(val)) {
                        val.forEach((item) => {
                            if (item !== undefined && item !== null) {
                                formData.append(`${key}[]`, item);
                            }
                        });
                    } else {
                        formData.append(key, val);
                    }
                });
                rawFiles.forEach((f) => {
                    formData.append("cv_file[]", f);
                });
                formData.append("_method", "PUT");
                call = ApiService.upload(`job-recruitment/cvs/${id}`, formData);
            } else {
                call = ApiService.update("job-recruitment/cvs", id, { ...updateRest, cv_file: typeof cv_file === "string" ? cv_file : "" });
            }

            call.then(({ data }) => {
                if (data && (data.error === true || data.error === 1 || data.status === false || data.error_code || (data.data && data.data.message_validate_form) || data.message_validate_form)) {
                    reject(data);
                    return;
                }
                const item = normalizeCvItem(data.data || data);
                context.commit(RCV_UPSERT_CV, item);
                resolve(data);
            }).catch((err) => {
                const res = (err && err.response) || err;
                const resData = (res && res.data) || res;
                if (
                    resData &&
                    (resData.message_validate_form ||
                        (resData.data && resData.data.message_validate_form) ||
                        resData.error ||
                        resData.message ||
                        resData.msg ||
                        resData.errors ||
                        (res && [400, 422, 403, 409, 500].includes(res.status)))
                ) {
                    reject(res);
                    return;
                }
                ApiService.update("job-recruitment-cvs", id, { ...rest, cv_file })
                    .then(({ data }) => {
                        if (data && (data.error === true || data.error === 1 || data.status === false || data.error_code || (data.data && data.data.message_validate_form) || data.message_validate_form)) {
                            reject(data);
                            return;
                        }
                        const item = normalizeCvItem(data.data || data);
                        context.commit(RCV_UPSERT_CV, item);
                        resolve(data);
                    })
                    .catch((err2) => reject((err2 && err2.response) || err2 || res));
            });
        });
    },

    [RCV_DELETE_CV](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            ApiService.delete("job-recruitment/cvs", id)
                .then(({ data }) => {
                    context.commit(RCV_REMOVE_CV, id);
                    resolve(data);
                })
                .catch(() => {
                    ApiService.delete("job-recruitment-cvs", id)
                        .then(({ data }) => {
                            context.commit(RCV_REMOVE_CV, id);
                            resolve(data);
                        })
                        .catch((err) => reject(err));
                });
        });
    },

    [RCV_UPDATE_CV_STATUS](context, payload) {
        const { id, candidate, ...rest } = payload || {};
        const targetId = id || (candidate && candidate.id);
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/cvs/${targetId}/status`, rest)
                .then(({ data }) => {
                    if (data && (data.error === true || data.status === false || data.error_code)) {
                        reject(data);
                        return;
                    }
                    const rawItem = data.data || data;
                    const item = normalizeCvItem(rawItem);
                    if (item) {
                        context.commit(RCV_UPSERT_CV, item);
                    }
                    resolve(data);
                })
                .catch((err) => {
                    const res = err && (err.response || err);
                    reject(res);
                });
        });
    },

    [RCV_GET_CV_STATUS](context) {
        return new Promise((resolve) => {
            const endpoints = [
                "cv-status",
                "job-recruitment/cv-status",
                "job-recruitment-cv-status",
                "noibo/cv-status",
                "noibo/job-recruitment/cv-status",
            ];

            const tryEndpoint = (idx) => {
                if (idx >= endpoints.length) {
                    resolve(STATUS_TEXT);
                    return;
                }
                const ep = endpoints[idx];
                ApiService.get(ep)
                    .then(({ data }) => {
                        if (data && (data.error === true || data.status === false)) {
                            tryEndpoint(idx + 1);
                            return;
                        }
                        const parsed = parseCvStatusResponse(data);
                        if (parsed && Object.keys(parsed).length > 0) {
                            updateStatusTextFromApi(parsed);
                            context.commit(RCV_SET_CV_STATUS_MAP, parsed);
                            resolve(parsed);
                        } else {
                            tryEndpoint(idx + 1);
                        }
                    })
                    .catch(() => {
                        tryEndpoint(idx + 1);
                    });
            };

            tryEndpoint(0);
        });
    },
};

const mutations = {
    [RCV_SET_CVS](state, list) {
        state.cvList = Array.isArray(list) ? list : [];
    },
    [RCV_SET_PAGINATION](state, data) {
        if (!data) return;
        state.pagination = {
            total: data.total || 0,
            currentPage: data.currentPage || data.current_page || 1,
            perPage: data.perPage || data.per_page || 15,
            lastPage: data.lastPage || data.last_page || 1,
        };
    },
    [RCV_SET_CURRENT_CV](state, item) {
        state.currentCv = item;
    },
    [RCV_SET_LOADING](state, value) {
        state.loading = !!value;
    },
    [RCV_UPSERT_CV](state, item) {
        if (!item || !item.id) return;
        const idx = state.cvList.findIndex((c) => String(c.id) === String(item.id));
        if (idx === -1) {
            state.cvList.unshift(item);
        } else {
            state.cvList.splice(idx, 1, { ...state.cvList[idx], ...item });
        }
    },
    [RCV_REMOVE_CV](state, id) {
        state.cvList = state.cvList.filter((c) => String(c.id) !== String(id));
    },
    [RCV_SET_CV_STATUS_MAP](state, map) {
        state.cvStatusMap = map || {};
        if (state.cvList && state.cvList.length > 0 && map) {
            state.cvList = state.cvList.map((item) => {
                const num = Number(item.status);
                return {
                    ...item,
                    status_text: map[num] || STATUS_TEXT[num] || item.status_text,
                };
            });
        }
    },
};

export default {
    state,
    actions,
    mutations,
    getters,
};
